/**
 * 開発用チューニング — `crt`・`camera` の既定値。CSS 変数は applyDevConfig が適用します。
 */

/**
 * @typedef {{
 *   facingMode: string,
 *   facingModeFallbacks?: string[],
 *   widthIdeal: number,
 *   heightIdeal: number,
 * }} CameraConfig
 */

/** @typedef {typeof devConfig} DevConfig */

export const devConfig = {
  /**
   * PC でも方位テープ・コンパス等を表示する（UI 調整用）。
   * false にすると従来どおりスマホ＋センサ時のみ HUD 表示。
   */
  ui: {
    /** true のとき PC でも方位・相対テープを表示（UI 調整用）。false では対応端末のみ */
    previewHudOnDesktop: false,
  },

  /**
   * 傾き（orientation β/γ）と揺れ（motion 加速度・角速度）の HUD 連動
   */
  motionLink: {
    /** false: 照準は CSS 中央固定（傾き・揺れで動かさない） */
    linkReticleToMotion: false,
    /** false: ミニマップは left/bottom 固定（視差で動かさない） */
    linkMapToMotion: false,
    /** ガンマ（左右傾き）1° あたりの照準横ズレ px（linkReticleToMotion が true のとき） */
    reticleTiltPxPerDeg: 0.42,
    /** ガンマに対する照準ロール角度の係数（上限は reticleRollClampDeg） */
    reticleRollPerGammaDeg: 0.28,
    reticleRollClampDeg: 7,
    /** ミニマップの視差は照準の何割か */
    mapParallaxScale: 0.28,
    shakeJitterScale: 3.8,
    /** 初期中立姿勢を決めるサンプル数 */
    calibrationFrames: 28,
    /** これ以上の加速度 (m/s²) で「揺れ検知」（線形加速度が取れる端末向け） */
    notifyShakeAccel: 16,
    /** これ以上の角速度ベクトル大きさ (°/s) でも検知（加速度が無い端末のフォールバック） */
    notifyRotationRateDegPerSec: 220,
    notifyCooldownMs: 2800,
    notifyMessageMs: 1400,
    notifyVibrateMs: 12,
  },

  /**
   * getUserMedia の希望解像度・向き。
   * `environment` = 背面（外向き）カメラを優先。未搭載・拒否時は `facingModeFallbacks` を順に試す。
   */
  camera: {
    facingMode: "environment",
    facingModeFallbacks: ["user"],
    widthIdeal: 1280,
    heightIdeal: 720,
  },

  crt: {
    /** ビネット（端の暗がり） */
    vignette: {
      /** 楕円サイズ（%） */
      ellipseWPercent: 78,
      ellipseHPercent: 68,
      centerXPercent: 50,
      centerYPercent: 45,
      /** グラデが透明→暗く切り替わる位置 */
      innerStopPercent: 48,
      /** 外周の黒の濃さ 0〜1 */
      edgeOpacity: 0.22,
      /** 内側に足すぼかし影の半径 px */
      insetBlurPx: 100,
      insetShadowOpacity: 0.28,
    },

    /** 横スキャンライン */
    scanlines: {
      /** レイヤー全体の不透明度 */
      layerOpacity: 0.32,
      /** 線の黒の強さ 0〜1 */
      lineDarken: 0.66,
      lineHeightPx: 1,
      /** 線の下の透明の幅 px（線＋隙間の周期） */
      gapBelowLinePx: 2,
    },

    /** Canvas 粒ノイズの上に載せるレイヤー（CSS） */
    noiseLayer: {
      opacity: 0.42,
    },

    /**
     * Canvas で描くグレインノイズ
     * cellPx が大きいほど粗く・粒子が目立ちにくい。alpha はランダム範囲。
     */
    canvasNoise: {
      cellPx: 6,
      alphaMin: 18,
      alphaMax: 54,
    },

    /** 全体のちらつき */
    flicker: {
      overlayDarken: 0.015,
      /** 1 周の時間（秒）。小さいほどテンポ速い */
      durationSec: 0.35,
      key0Opacity: 0.94,
      key50Opacity: 1,
      key100Opacity: 0.9,
      /** prefers-reduced-motion 時の固定に近い不透明度 */
      reducedMotionOpacity: 0.96,
    },

    /** 横一瞬グリッチ（本数・間隔・ピーク強さ） */
    glitch: {
      stripA: {
        durationSec: 8.5,
        topPercent: 22,
        whiteA: 0.06,
        greenA: 0.03,
        key95: 0.45,
        key96: 0.28,
        tx95Percent: -2,
        tx96Percent: 1,
      },
      stripB: {
        durationSec: 11,
        topPercent: 48,
        redA: 0.04,
        whiteA: 0.05,
        key97: 0.4,
        key98: 0.22,
        tx97Percent: 2,
        tx98Percent: -1,
        skew97Deg: -0.35,
        skew98Deg: 0.25,
      },
      stripC: {
        durationSec: 9.8,
        topPercent: 71,
        blueA: 0.035,
        whiteA: 0.045,
        key96: 0.42,
        key97: 0.18,
        tx96Percent: -3,
        tx97Percent: 1.5,
      },
    },
  },

  /**
   * TensorFlow.js 推論バックエンド（`src/tfBackend.js`）。
   * 既定は WebGPU → WebGL → CPU。未対応端末では自動フォールバック。
   */
  tfBackend: {
    tryWebGpu: true,
    preferOrder: ["webgpu", "webgl", "cpu"],
  },

  /**
   * ライブ中の軽量ヘルス監視（CPU 監視なし・既存ループからの所要時間のみ）。
   */
  liveHealth: {
    enabled: true,
    /** 推論予算 = intervalMs × この倍率を超えたら遅延カウント */
    inferSlowRatio: 2.2,
    /** 遅延がこの回数連続でアラート */
    inferSlowStreak: 3,
    /** 推論例外がこの回数連続でアラート */
    inferErrorStreak: 2,
    /** currentTime が動かない秒数で映像ストール */
    videoStallMs: 2200,
    /** 映像チェック間隔 ms */
    videoCheckMs: 900,
    /** 同種アラートの最短間隔 ms */
    alertCooldownMs: 28000,
    /** ブート時モデル読込の上限 ms */
    modelLoadTimeoutMs: 120000,
  },

  /**
   * 物体検出（coco-ssd / COCO 80 クラス）。軽さ優先なら base・maxSidePx・intervalMs を抑える。
   */
  detection: {
    /** lite_mobilenet_v2: 最軽量 | mobilenet_v2: やや重いが精度↑ */
    ssdBase: "lite_mobilenet_v2",
    /** 推論に使う映像の長辺の上限 px（小さいほど軽い・粗い） */
    maxSidePx: 320,
    /** 推論間隔 ms（大きいほど CPU/GPU 負荷↓）。対応ブラウザでは映像フレームに同期して間引き */
    intervalMs: 140,
    /**
     * 枠の対象:
     * - `"all"` … COCO の検出クラスを scoreMin 以上はすべて（種類はモデル依存・約80）
     * - `["person","cat"]` … 列挙したクラスのみ（英語・スペース含め coco-ssd と完全一致）
     */
    targetClasses: "all",
    /** 1 フレームあたり返すボックス上限（多物体シーンで調整） */
    maxBoxes: 60,
    /** 採用する最低スコア 0〜1（detect にそのまま渡す） */
    scoreMin: 0.5,
    /**
     * ロックオン範囲：画面中央の正方形（4 コーナーのみ表示）の一辺 ≒ 画面の長辺 × この比率（0.4 = 40%）。
     */
    lockZoneScreenRatio: 0.4,
    /**
     * ロック照準内の小さい四角（中央照準の CSS 基準幅に対する一辺の比率）。
     */
    lockInnerReticleScale: 0.28,
    /**
     * 内側レティクルがターゲット中心へ追従するときの時定数 ms（大きいほど時間差・なめらかに遅れる）。
     */
    lockTrackTauMs: 520,
    /** ロックゾーン内に対象が連続で滞在したとみなす時間 ms → ロックオン（赤枠＋◇） */
    lockDwellMs: 1600,
    /**
     * ロック用キー（座標の粗い丸め）の 1 辺 px。大きいほどブレに強くロックが安定しやすい。
     */
    lockTrackQuantPx: 64,
    /**
     * 前フレームの同一キー中心から何 px 以内の検出を、同じターゲットとしてキー継続するか。
     * 0 のとき距離照合を行わない（従来どおり量子化キーのみ）。
     */
    lockMatchMaxDistPx: 120,
    /**
     * 45°◇の一辺の基準は innerSide/√2（◇の角が正立内枠の各辺中央に来るサイズ）。微調整のみ 1 未満。
     */
    lockMicroDiamondFitRatio: 1,
    /** ロックゾーン内で同時に追従・ロック表示するターゲット数の上限 */
    lockMaxConcurrent: 2,

    /**
     * 内部推定モード（UI の距離表示には使わない。WebXR 実測のみ表示）。
     * `bbox` / `dummy_const` … 将来のロジック用の内部値。
     */
    rangeMode: "bbox",
    /** bbox 推定: 距離 ≒ refM * rangeCalibrationK * (画面短辺 / bbox高さ) */
    rangeCalibrationK: 0.38,
    /** 未登録クラスに使う仮想実寸 (m) */
    rangeDefaultRefM: 0.55,
    rangeClampMinM: 0.35,
    rangeClampMaxM: 24,
    /** `dummy_const` 時の基準距離 (m) */
    rangeDummyConstM: 3.8,
    rangeDummyWobbleM: 0.25,
    /**
     * WebXR immersive-ar + hit-test で実距離を試みる（対応端末のみ UI に距離が出る）。
     * false にすると距離表示は行わない。
     */
    enableWebXrRangeSensor: true,
    /** センサー距離サンプルを「有効」とみなす最大経過時間 (ms) */
    rangeHardwareMaxAgeMs: 520,
  },

  /**
   * BlazeFace の顔矩形いっぱいにモザイク（矩形全体を均等ブロックで覆う）。任意で軽いグリッチ。
   * デモ用（スクショ取得後は機能ごと除外予定 — docs/LIVE_HUD_AND_CAMERA_SPEC.md §6）。
   */
  facePrivacy: {
    enabled: true,
    /** 推論に使う長辺の上限 px */
    maxSidePx: 320,
    /** 顔推論の間隔 ms（130→260: 負荷半減・デモ向け。追従はやや遅れる） */
    intervalMs: 260,
    /** 同時に扱う顔の最大数 */
    maxFaces: 5,
    /** BlazeFace 検出しきい値 0〜1 */
    scoreThreshold: 0.72,
    /** 映像とモデルの向き（鏡面プレビューでずれるとき true を試す） */
    flipHorizontal: false,
    /** 検出矩形を外側に広げる割合（顔全体が確実に矩形に収まるように） */
    boxPaddingRatio: 0.28,
    /** 画面座標で矩形をさらに拡大（顔周りをモザイクで包む）。1 で無効 */
    boxEffectOutsetScale: 1.12,
    /** 長辺がこれ px を超えるとパッチを縮小してから加工 */
    maxPatchSidePx: 420,
    /** モザイクのブロック一辺 px（矩形全体をこの粒度で塗りつぶす）。大きいほど軽い・粗い */
    mosaicBlockPx: 22,
    /**
     * true のときだけモザイクに加え軽いグリッチ（負荷↑）。
     * 複数人数・モバイルでは false 推奨。
     */
    useGlitch: false,
    glitch: {
      tearBands: 5,
      rgbShiftPx: 4,
      noiseDensity: 0.08,
    },
    /** prefers-reduced-motion 時はモザイクのみ（ブロックをやや大きく） */
    reducedMosaicBlockPx: 28,
  },
};
